import { useMemo } from "react";
import { format, addDays, isSameDay, isBefore, isAfter, startOfDay, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, Calendar, CheckCircle2, Clock, Flag, Unlock, Wallet, Zap } from "lucide-react";
import { cn, formatBRL } from "@/lib/utils";

interface OrderItem {
  id: string;
  event_id: string;
  total: number;
  platform_fee?: number | null;
  payment_gateway_fee?: number | null;
  refunded_amount?: number | null;
  payment_method: string;
  status: string;
  payment_status: string;
  paid_at?: string | null;
  created_at?: string | null;
}

interface TimelinePoint {
  label: string;
  revenue: number;
  orders: number;
}

interface BalanceItem {
  event_id: string;
  event_title: string;
  event_end?: string | null;
  card_total?: number;
  credit_card_total?: number;
  boleto_total?: number;
  pix_pending_release?: number;
  pix_pending?: number;
  pix_released?: number;
  available?: number;
  total_withdrawn?: number;
  post_event_release?: string | null;
  post_event_released?: boolean;
}

interface ProducerWithdrawalGanttProps {
  summary: {
    pix: number;
    pixReleased: number;
    pixPending: number;
    creditCard: number;
    boleto: number;
    available: number;
    pending: number;
    withdrawn: number;
  };
  netSales: number;
  toRelease?: number;
  selectedEvent?: {
    event_id: string;
    event_title: string;
    event_end: string | null;
    start_date?: string | null;
    post_event_released?: boolean;
    post_event_release?: string | null;
  } | null;
  nextPixRelease?: string | null;
  nextEventRelease?: string | null;
  allPostEventReleased?: boolean;
  balances?: BalanceItem[];
  orders?: OrderItem[];
  timelineByDay?: TimelinePoint[];
  withdrawals?: Array<{
    id: string;
    amount: number;
    status: string;
    requested_at: string;
    bank_name: string | null;
    account_number: string | null;
    events: { title: string } | null;
    event_id?: string | null;
  }>;
  onOpenRulesModal: () => void;
}

function formatCompactBRL(value: number) {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1)}k`;
  return `R$ ${Math.round(value)}`;
}

function formatBadgeBRL(value: number) {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1).replace(".", ",")}M`;
  if (value >= 100_000) return `R$ ${Math.round(value / 1_000)}k`;
  if (value % 1 !== 0 && value < 1000) {
    return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `R$ ${Math.round(value).toLocaleString("pt-BR")}`;
}

function truncateText(str: string, maxLen: number = 18) {
  if (!str) return "";
  return str.length > maxLen ? `${str.slice(0, maxLen - 1)}…` : str;
}

export function ProducerWithdrawalGantt({
  summary,
  netSales,
  toRelease = 0,
  selectedEvent,
  balances = [],
  orders = [],
  withdrawals = [],
  onOpenRulesModal,
}: ProducerWithdrawalGanttProps) {
  const today = useMemo(() => startOfDay(new Date()), []);

  // Mapeamento de id de evento para título para reconciliação precisa
  const eventTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of balances) {
      if (b.event_id && b.event_title) {
        map.set(b.event_id, b.event_title);
      }
    }
    if (selectedEvent?.event_id && selectedEvent.event_title) {
      map.set(selectedEvent.event_id, selectedEvent.event_title);
    }
    return map;
  }, [balances, selectedEvent]);

  // Valor retido / a liberar do evento selecionado ou consolidado
  const toReleaseAmount = useMemo(() => {
    if (selectedEvent) {
      const b = balances.find((bal) => bal.event_id === selectedEvent.event_id);
      if (b) {
        return Number(b.pix_pending_release || 0) + (b.post_event_released ? 0 : Number(b.credit_card_total ?? b.card_total ?? 0) + Number(b.boleto_total || 0));
      }
    }
    return toRelease;
  }, [selectedEvent, balances, toRelease]);

  // 1. Filtrar saques do escopo (evento selecionado ou todos)
  const relevantWithdrawals = useMemo(() => {
    return selectedEvent
      ? withdrawals.filter(
          (w) =>
            w.event_id === selectedEvent.event_id ||
            w.events?.title === selectedEvent.event_title ||
            (withdrawals.length === 1 && summary.withdrawn > 0)
        )
      : withdrawals;
  }, [withdrawals, selectedEvent, summary.withdrawn]);

  // 2. Data de término do evento (real) - para evento individual selecionado
  const eventEndDate = useMemo(() => {
    if (!selectedEvent || !selectedEvent.event_end) return null;
    return startOfDay(new Date(selectedEvent.event_end));
  }, [selectedEvent]);

  const postEventReleaseDate = useMemo(() => (eventEndDate ? addDays(eventEndDate, 1) : null), [eventEndDate]);
  const isRetroactiveEvent = useMemo(() => Boolean(eventEndDate && isBefore(eventEndDate, today)), [eventEndDate, today]);

  // 3. Mapeamento de destravamento de todos os eventos
  const allEventReleases = useMemo(() => {
    return balances
      .map((b) => {
        const amount = Number(b.credit_card_total ?? b.card_total ?? 0) + Number(b.boleto_total || 0);
        const end = b.event_end ? startOfDay(new Date(b.event_end)) : null;
        const rel = end ? addDays(end, 1) : null;
        const isPast = Boolean(end && isBefore(end, today));
        return {
          id: b.event_id,
          title: b.event_title,
          amount,
          endDate: end,
          releaseDate: rel,
          isPast,
          released: b.post_event_released || isPast,
        };
      })
      .filter((item) => item.endDate)
      .sort((a, b) => a.endDate!.getTime() - b.endDate!.getTime());
  }, [balances, today]);

  // 4. Construção da série temporal de Saldo Disponível ao Longo do Tempo
  const { chartData, todayLabel, eventEndLabel, postEventReleaseLabel, peakAvailable, maxChartY, finalProjectedAmount, upcomingReleases, withdrawalGroups } = useMemo(() => {
    // A. Mapear Entradas (Releases) por data
    interface EventReleaseItem {
      eventId: string;
      eventTitle: string;
      amount: number;
    }

    const releasesMap: Record<
      string,
      {
        pix: number;
        pixBreakdown: EventReleaseItem[];
        card: number;
        cardBreakdown: EventReleaseItem[];
        eventNames: string[];
      }
    > = {};

    const relevantOrders = selectedEvent ? orders.filter((o) => o.event_id === selectedEvent.event_id) : orders;

    let minDate: Date | null = null;

    // Processar pedidos PIX
    if (relevantOrders.length > 0) {
      for (const o of relevantOrders) {
        const raw = o.paid_at || o.created_at;
        if (!raw) continue;
        const paidDate = startOfDay(new Date(raw));
        if (!minDate || isBefore(paidDate, minDate)) minDate = paidDate;

        const net = Math.max(0, Number(o.total || 0) - Number(o.refunded_amount || 0) - Number(o.platform_fee || 0) - Number(o.payment_gateway_fee || 0));

        if (o.payment_method === "pix") {
          const relDate = addDays(paidDate, 2); // PIX D+2
          const key = format(relDate, "yyyy-MM-dd");
          if (!releasesMap[key]) {
            releasesMap[key] = { pix: 0, pixBreakdown: [], card: 0, cardBreakdown: [], eventNames: [] };
          }
          releasesMap[key].pix += net;

          const evTitle = eventTitleMap.get(o.event_id) || selectedEvent?.event_title || "Evento";
          const existing = releasesMap[key].pixBreakdown.find((item) => item.eventId === o.event_id);
          if (existing) {
            existing.amount += net;
          } else {
            releasesMap[key].pixBreakdown.push({
              eventId: o.event_id,
              eventTitle: evTitle,
              amount: net,
            });
          }

          if (!releasesMap[key].eventNames.includes(evTitle)) {
            releasesMap[key].eventNames.push(evTitle);
          }
        }
      }
    }

    // Processar destravamento de Cartão e Boleto de cada evento
    const relevantBalances = selectedEvent ? balances.filter((b) => b.event_id === selectedEvent.event_id) : balances;
    for (const b of relevantBalances) {
      const cardBoleto = Number(b.credit_card_total ?? b.card_total ?? 0) + Number(b.boleto_total || 0);
      if (b.event_end && cardBoleto > 0) {
        const end = startOfDay(new Date(b.event_end));
        if (!minDate || isBefore(end, minDate)) minDate = addDays(end, -30);
        const rel = addDays(end, 1);
        const key = format(rel, "yyyy-MM-dd");
        if (!releasesMap[key]) {
          releasesMap[key] = { pix: 0, pixBreakdown: [], card: 0, cardBreakdown: [], eventNames: [] };
        }
        releasesMap[key].card += cardBoleto;

        const existing = releasesMap[key].cardBreakdown.find((item) => item.eventId === b.event_id);
        if (existing) {
          existing.amount += cardBoleto;
        } else {
          releasesMap[key].cardBreakdown.push({
            eventId: b.event_id,
            eventTitle: b.event_title,
            amount: cardBoleto,
          });
        }

        if (!releasesMap[key].eventNames.includes(b.event_title)) {
          releasesMap[key].eventNames.push(b.event_title);
        }
      }
    }

    // Se o evento é retroativo e teve saques (ex: Nexus 2.0), mas não tínhamos pedidos granulares suficientes:
    // Garantir que a receita líquida total desse evento entrou no dia de liberação pós-evento!
    if (selectedEvent && postEventReleaseDate && isRetroactiveEvent) {
      const postKey = format(postEventReleaseDate, "yyyy-MM-dd");
      const totalEventRevenue = Math.max(
        summary.available + summary.withdrawn + summary.pending,
        netSales
      );
      if (totalEventRevenue > 0) {
        let sumReleases = 0;
        for (const k in releasesMap) sumReleases += releasesMap[k].pix + releasesMap[k].card;
        if (sumReleases < totalEventRevenue) {
          const diff = totalEventRevenue - sumReleases;
          if (!releasesMap[postKey]) {
            releasesMap[postKey] = { pix: 0, pixBreakdown: [], card: 0, cardBreakdown: [], eventNames: [] };
          }
          releasesMap[postKey].card += diff;
          const existing = releasesMap[postKey].cardBreakdown.find((item) => item.eventId === selectedEvent.event_id);
          if (existing) {
            existing.amount += diff;
          } else {
            releasesMap[postKey].cardBreakdown.push({
              eventId: selectedEvent.event_id,
              eventTitle: selectedEvent.event_title,
              amount: diff,
            });
          }
          if (!releasesMap[postKey].eventNames.includes(selectedEvent.event_title)) {
            releasesMap[postKey].eventNames.push(selectedEvent.event_title);
          }
        }
      }
    }

    // Se o evento está selecionado e é ativo/futuro, garantir que o valor retido a liberar entre na data de liberação pós-evento (+1d)
    if (selectedEvent && postEventReleaseDate && !isRetroactiveEvent && toReleaseAmount > 0) {
      const postKey = format(postEventReleaseDate, "yyyy-MM-dd");
      if (!releasesMap[postKey]) {
        releasesMap[postKey] = { pix: 0, pixBreakdown: [], card: 0, cardBreakdown: [], eventNames: [] };
      }
      if (releasesMap[postKey].card === 0) {
        releasesMap[postKey].card = toReleaseAmount;
        releasesMap[postKey].cardBreakdown.push({
          eventId: selectedEvent.event_id,
          eventTitle: selectedEvent.event_title,
          amount: toReleaseAmount,
        });
        if (!releasesMap[postKey].eventNames.includes(selectedEvent.event_title)) {
          releasesMap[postKey].eventNames.push(selectedEvent.event_title);
        }
      }
    }

    // B. Mapear Saídas (Withdrawals/Resgates) por data com evento de origem
    const withdrawalsMap: Record<string, number> = {};
    const withdrawalsDetailsMap: Record<
      string,
      Array<{
        id?: string;
        amount: number;
        time?: string;
        bankName?: string | null;
        accountNumber?: string | null;
        fullDate: string;
        eventId?: string | null;
        eventTitle: string;
      }>
    > = {};

    for (const w of relevantWithdrawals) {
      if (["paid", "completed", "processing", "requested", "under_review"].includes(w.status)) {
        const wDate = startOfDay(new Date(w.requested_at));
        if (!minDate || isBefore(wDate, minDate)) minDate = addDays(wDate, -7);
        const key = format(wDate, "yyyy-MM-dd");
        withdrawalsMap[key] = (withdrawalsMap[key] || 0) + Number(w.amount || 0);

        const evTitle =
          w.events?.title ||
          (w.event_id ? eventTitleMap.get(w.event_id) : null) ||
          selectedEvent?.event_title ||
          "Geral";

        if (!withdrawalsDetailsMap[key]) withdrawalsDetailsMap[key] = [];
        withdrawalsDetailsMap[key].push({
          id: w.id,
          amount: Number(w.amount || 0),
          time: w.requested_at ? format(new Date(w.requested_at), "HH:mm") : undefined,
          bankName: w.bank_name,
          accountNumber: w.account_number,
          fullDate: format(wDate, "dd/MM/yyyy"),
          eventId: w.event_id,
          eventTitle: evTitle,
        });
      }
    }

    // C. Determinar Início e Fim da Linha do Tempo
    let historyStart = minDate ? addDays(minDate, -2) : addDays(today, -30);
    if (isBefore(historyStart, addDays(today, -120))) {
      historyStart = addDays(today, -120);
    }

    let futureEnd = addDays(today, 14);
    if (selectedEvent && eventEndDate) {
      futureEnd = isBefore(eventEndDate, today) ? addDays(today, 3) : addDays(eventEndDate, 4);
    } else if (allEventReleases.length > 0) {
      let maxEnd: Date | null = null;
      for (const ev of allEventReleases) {
        if (ev.endDate && !ev.isPast) {
          if (!maxEnd || isAfter(ev.endDate, maxEnd)) maxEnd = ev.endDate;
        }
      }
      futureEnd = maxEnd ? addDays(maxEnd, 4) : addDays(today, 14);
    }
    // Permitir horizonte de até 365 dias para contemplar eventos futuros agendados
    if (isAfter(futureEnd, addDays(today, 365))) futureEnd = addDays(today, 365);

    // D. Simulação contínua do Saldo Disponível dia a dia
    const points = [];
    let cur = new Date(historyStart);
    let todayLabelStr = "";
    let eventEndLabelStr = "";
    let postEventReleaseLabelStr = "";
    let runningBalance = 0;
    let peak = 0;

    const finalAmount = Math.max(summary.available, netSales - summary.withdrawn, summary.available + toReleaseAmount);

    while (!isAfter(cur, futureEnd) && points.length < 400) {
      const dateKey = format(cur, "yyyy-MM-dd");
      const label = format(cur, "dd 'de' MMM", { locale: ptBR });
      const fullDate = format(cur, "EEEE, dd 'de' MMMM", { locale: ptBR });
      const isPast = isBefore(cur, today);
      const isCurToday = isSameDay(cur, today);
      const isFuture = isAfter(cur, today);

      const isEventEnd = Boolean(eventEndDate && isSameDay(cur, eventEndDate));
      const isPostEventRelease = Boolean(postEventReleaseDate && isSameDay(cur, postEventReleaseDate));

      if (isCurToday) todayLabelStr = label;
      if (isEventEnd) eventEndLabelStr = label;
      if (isPostEventRelease) postEventReleaseLabelStr = label;

      let pixReleases = 0;
      let cardReleases = 0;
      const releasingEventNames: string[] = [];

      // 1. Somar entradas de saldo disponíveis no dia
      if (releasesMap[dateKey]) {
        pixReleases = releasesMap[dateKey].pix;
        cardReleases = releasesMap[dateKey].card;
        releasingEventNames.push(...releasesMap[dateKey].eventNames);
        runningBalance += (pixReleases + cardReleases);
      }

      // 2. Subtrair resgates efetuados no dia (o saldo DESCE!)
      const dayWd = withdrawalsMap[dateKey] || 0;
      if (dayWd > 0) {
        runningBalance = Math.max(0, runningBalance - dayWd);
      }

      peak = Math.max(peak, runningBalance);

      // Calibração: hoje o saldo disponível é exatamente summary.available
      let displayValue = runningBalance;
      if (isCurToday) {
        displayValue = summary.available;
        runningBalance = summary.available;
      } else if (isFuture) {
        displayValue = runningBalance;
      }

      const dayReleases = releasesMap[dateKey] || {
        pix: 0,
        pixBreakdown: [],
        card: 0,
        cardBreakdown: [],
        eventNames: [],
      };
      const dayWdList = withdrawalsDetailsMap[dateKey] || [];

      points.push({
        label,
        fullDate,
        isFuture,
        isEventEnd,
        isPostEventRelease,
        tooltipValue: Math.max(0, displayValue),
        realized: isPast || isCurToday ? Math.max(0, displayValue) : null,
        projected: isPast ? null : Math.max(0, displayValue),
        pixReleases: dayReleases.pix,
        pixBreakdown: dayReleases.pixBreakdown,
        cardReleases: dayReleases.card,
        cardBreakdown: dayReleases.cardBreakdown,
        withdrawalsOnDay: dayWd,
        withdrawalsList: dayWdList,
        releasingEventNames: dayReleases.eventNames,
      });

      cur = addDays(cur, 1);
    }

    const maxDomainY = Math.max(peak * 1.15, finalAmount * 1.15, (summary.available + toReleaseAmount) * 1.15, 100);

    // E. Agrupamento de saques por data para marcadores com anti-sobreposição total
    const groupsMap = new Map<
      string,
      {
        dateLabel: string;
        dateKey: string;
        fullDate: string;
        wDate: Date;
        withdrawals: Array<{
          id?: string;
          amount: number;
          time?: string;
          bankName?: string | null;
          accountNumber?: string | null;
          fullDate: string;
          eventId?: string | null;
          eventTitle: string;
        }>;
        totalAmount: number;
      }
    >();

    for (const [dateKey, list] of Object.entries(withdrawalsDetailsMap)) {
      if (list.length === 0) continue;
      const wDate = startOfDay(new Date(dateKey + "T00:00:00"));
      const dateLabel = format(wDate, "dd 'de' MMM", { locale: ptBR });
      const fullDate = format(wDate, "dd/MM/yyyy");
      const total = list.reduce((acc, curr) => acc + curr.amount, 0);

      groupsMap.set(dateKey, {
        dateLabel,
        dateKey,
        fullDate,
        wDate,
        // Ordenar saques do dia pelo maior valor na base e menor empilhado acima
        withdrawals: [...list].sort((a, b) => b.amount - a.amount),
        totalAmount: total,
      });
    }

    const sortedGroups = Array.from(groupsMap.values()).sort(
      (a, b) => a.wDate.getTime() - b.wDate.getTime()
    );

    const labelToIndex = new Map<string, number>();
    points.forEach((pt, idx) => {
      if (!labelToIndex.has(pt.label)) {
        labelToIndex.set(pt.label, idx);
      }
    });

    const activeWithdrawalGroups: Array<{
      dateLabel: string;
      dateKey: string;
      fullDate: string;
      dayIndex: number;
      totalAmount: number;
      withdrawals: Array<{
        id?: string;
        amount: number;
        time?: string;
        bankName?: string | null;
        accountNumber?: string | null;
        fullDate: string;
        eventId?: string | null;
        eventTitle: string;
      }>;
      baseLevel: number;
    }> = [];

    for (const g of sortedGroups) {
      const idx = labelToIndex.get(g.dateLabel);
      if (idx !== undefined) {
        activeWithdrawalGroups.push({
          dateLabel: g.dateLabel,
          dateKey: g.dateKey,
          fullDate: g.fullDate,
          dayIndex: idx,
          totalAmount: g.totalAmount,
          withdrawals: g.withdrawals,
          baseLevel: 0,
        });
      }
    }

    // Prevenção de colisão horizontal entre datas próximas (< 7 dias)
    for (let i = 0; i < activeWithdrawalGroups.length; i++) {
      const current = activeWithdrawalGroups[i];
      if (i > 0) {
        const prev = activeWithdrawalGroups[i - 1];
        const dayDiff = current.dayIndex - prev.dayIndex;
        if (dayDiff < 7) {
          const prevTopLevel = prev.baseLevel + Math.min(prev.withdrawals.length, 3);
          current.baseLevel = prevTopLevel % 3;
        } else {
          current.baseLevel = 0;
        }
      } else {
        current.baseLevel = 0;
      }
    }

    return {
      chartData: points,
      todayLabel: todayLabelStr,
      eventEndLabel: eventEndLabelStr,
      postEventReleaseLabel: postEventReleaseLabelStr,
      peakAvailable: Math.max(peak, summary.available + toReleaseAmount),
      maxChartY: maxDomainY,
      finalProjectedAmount: finalAmount,
      upcomingReleases: allEventReleases.filter((e) => !e.isPast && e.amount > 0).slice(0, 3),
      withdrawalGroups: activeWithdrawalGroups,
    };
  }, [selectedEvent, orders, balances, relevantWithdrawals, postEventReleaseDate, eventEndDate, summary, netSales, today, allEventReleases, isRetroactiveEvent, toReleaseAmount, eventTitleMap]);

  return (
    <div className="space-y-4">
      {/* Header Apple Minimalist: Resumo sutil e tipografia refinada */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 pb-1 border-b border-border/25">
        <div>
          <p className="text-xs font-medium text-muted-foreground">
            {selectedEvent ? selectedEvent.event_title : "Todos os eventos"} · Saldo disponível ao longo do tempo
          </p>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="font-display text-2xl font-semibold tracking-tight text-foreground tabular-nums">
              {formatBRL(summary.available)}
            </span>
            <span className="text-xs text-muted-foreground">disponível hoje</span>
            {summary.withdrawn > 0 && (
              <>
                <span className="text-xs text-muted-foreground/50">/</span>
                <span className="text-xs font-medium text-foreground tabular-nums">
                  {formatBRL(summary.withdrawn)}
                </span>
                <span className="text-xs text-muted-foreground">já resgatado</span>
              </>
            )}
            {peakAvailable > summary.available && (
              <span className="hidden sm:inline-block text-[11px] text-muted-foreground/80">
                (Pico histórico: {formatBRL(peakAvailable)})
              </span>
            )}
          </div>
          {/* Badges de data de término e liberação para evento ativo */}
          {selectedEvent && eventEndDate && (
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-secondary/40 px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                <Flag className="h-3 w-3 text-muted-foreground" />
                <span>
                  Fim do evento: <strong>{format(eventEndDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}</strong>
                  {isRetroactiveEvent ? " (concluído)" : ` (em ${differenceInDays(eventEndDate, today)} dias)`}
                </span>
              </span>
              {postEventReleaseDate && !selectedEvent.post_event_released && toReleaseAmount > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                  <Unlock className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  <span>
                    Liberação pós-evento (+1d): <strong>{format(postEventReleaseDate, "dd 'de' MMMM", { locale: ptBR })}</strong> (+{formatBRL(toReleaseAmount)})
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onOpenRulesModal}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
        >
          Regras e prazos <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Gráfico Composed de Saldo Disponível */}
      <div className="h-64 sm:h-72 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240} debounce={50}>
          <ComposedChart data={chartData} margin={{ top: 22, right: 16, left: -14, bottom: 0 }}>
            <defs>
              <linearGradient id="appleRealizedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.18} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border) / 0.3)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={32}
            />
            <YAxis
              width={60}
              domain={[0, maxChartY]}
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => formatCompactBRL(Number(v))}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const pt = payload[0].payload;
                return (
                  <div className="rounded-2xl border border-border/80 bg-background/95 p-3 shadow-xl backdrop-blur-md text-xs min-w-[220px] space-y-1.5">
                    <p className="font-semibold text-foreground capitalize">{pt.fullDate}</p>
                    <div className="flex items-baseline justify-between gap-4 pt-1 border-t border-border/40">
                      <span className="text-muted-foreground">{pt.isFuture ? "Saldo previsto:" : "Saldo disponível:"}</span>
                      <span className="font-display font-semibold tabular-nums text-foreground">{formatBRL(pt.tooltipValue)}</span>
                    </div>

                    {/* 1. Resgates (Saques efetuados) */}
                    {pt.withdrawalsOnDay > 0 && (
                      <div className="rounded-xl bg-rose-500/10 border border-rose-500/20 p-2.5 text-[11px] text-rose-700 dark:text-rose-400 font-medium space-y-1.5">
                        <div className="flex items-center gap-1.5 font-semibold">
                          <ArrowDownRight className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
                          <span>
                            {pt.withdrawalsList && pt.withdrawalsList.length > 1
                              ? `Total resgates realizados (${pt.withdrawalsList.length}): -${formatBRL(pt.withdrawalsOnDay)}`
                              : `Resgate realizado: -${formatBRL(pt.withdrawalsOnDay)}`}
                          </span>
                        </div>

                        {/* Se 1 saque, exibe o evento e banco com clareza */}
                        {pt.withdrawalsList?.length === 1 && (
                          <div className="pl-5 text-[10px] text-rose-700/90 dark:text-rose-300 flex items-center justify-between gap-2">
                            <span className="truncate max-w-[190px]">
                              Evento: <strong>{pt.withdrawalsList[0].eventTitle}</strong>
                              {pt.withdrawalsList[0].bankName ? ` (${pt.withdrawalsList[0].bankName})` : ""}
                            </span>
                            {pt.withdrawalsList[0].time && (
                              <span className="text-muted-foreground text-[9px] shrink-0">
                                {pt.withdrawalsList[0].time}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Se múltiplos saques, lista o de cada um com evento e valor */}
                        {pt.withdrawalsList && pt.withdrawalsList.length > 1 && (
                          <div className="space-y-1 pt-1.5 border-t border-rose-500/20 text-[10px]">
                            {pt.withdrawalsList.map((w, wIdx: number) => (
                              <div key={wIdx} className="flex items-center justify-between text-rose-700/90 dark:text-rose-300 gap-2">
                                <span className="truncate max-w-[170px]">
                                  • {w.eventTitle}{w.bankName ? ` (${w.bankName})` : ""}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  {w.time && <span className="text-[9px] text-muted-foreground">{w.time}</span>}
                                  <span className="font-semibold tabular-nums">-{formatBRL(w.amount)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {pt.isEventEnd && (
                      <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400 font-medium flex items-center gap-1.5">
                        <Flag className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span>Término do evento</span>
                      </div>
                    )}

                    {/* 2. Liberações pós-evento (Cartão / Boleto) */}
                    {pt.cardReleases > 0 && (
                      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-2.5 text-[11px] text-emerald-700 dark:text-emerald-400 font-medium space-y-1.5">
                        <div className="flex items-center gap-1.5 font-semibold">
                          <Unlock className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <span>
                            {pt.cardBreakdown && pt.cardBreakdown.length > 1
                              ? `Total liberações pós-evento (${pt.cardBreakdown.length}): +${formatBRL(pt.cardReleases)}`
                              : `Liberação pós-evento: +${formatBRL(pt.cardReleases)}`}
                          </span>
                        </div>

                        {/* Se 1 evento */}
                        {pt.cardBreakdown?.length === 1 && (
                          <div className="pl-5 text-[10px] text-emerald-700/90 dark:text-emerald-300 truncate">
                            Evento: <strong>{pt.cardBreakdown[0].eventTitle}</strong>
                          </div>
                        )}

                        {/* Se múltiplos eventos, lista cada um */}
                        {pt.cardBreakdown && pt.cardBreakdown.length > 1 && (
                          <div className="space-y-1 pt-1.5 border-t border-emerald-500/20 text-[10px]">
                            {pt.cardBreakdown.map((item, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-emerald-700/90 dark:text-emerald-300 gap-2">
                                <span className="truncate max-w-[180px]">• {item.eventTitle}</span>
                                <span className="font-semibold tabular-nums shrink-0">+{formatBRL(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 3. Liberações PIX (D+2) */}
                    {pt.pixReleases > 0 && (
                      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-2.5 text-[11px] text-emerald-700 dark:text-emerald-400 font-medium space-y-1.5">
                        <div className="flex items-center gap-1.5 font-semibold">
                          <Zap className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <span>
                            {pt.pixBreakdown && pt.pixBreakdown.length > 1
                              ? `Total liberações PIX (D+2): +${formatBRL(pt.pixReleases)}`
                              : `+${formatBRL(pt.pixReleases)} liberação PIX (D+2)`}
                          </span>
                        </div>

                        {/* Se 1 evento */}
                        {pt.pixBreakdown?.length === 1 && (
                          <div className="pl-5 text-[10px] text-emerald-700/90 dark:text-emerald-300 truncate">
                            Evento: <strong>{pt.pixBreakdown[0].eventTitle}</strong>
                          </div>
                        )}

                        {/* Se múltiplos eventos, lista cada um */}
                        {pt.pixBreakdown && pt.pixBreakdown.length > 1 && (
                          <div className="space-y-1 pt-1.5 border-t border-emerald-500/20 text-[10px]">
                            {pt.pixBreakdown.map((item, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-emerald-700/90 dark:text-emerald-300 gap-2">
                                <span className="truncate max-w-[180px]">• {item.eventTitle}</span>
                                <span className="font-semibold tabular-nums shrink-0">+{formatBRL(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              }}
            />

            {/* Traçados das Áreas e Linhas (desenhadas primeiro para que as marcações fiquem sempre na camada superior / z-index à frente) */}
            <Area type="monotone" dataKey="realized" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#appleRealizedGradient)" connectNulls dot={false} />
            <Line type="monotone" dataKey="projected" stroke="hsl(var(--primary))" strokeWidth={2} strokeDasharray="4 4" connectNulls dot={false} />

            {/* Marcador vertical para "Hoje" (sempre na frente) */}
            {todayLabel && (
              <ReferenceLine
                x={todayLabel}
                stroke="hsl(var(--primary) / 0.5)"
                strokeDasharray="3 3"
                isFront={true}
                label={{ value: "Hoje", position: "insideTopLeft", fill: "hsl(var(--primary))", fontSize: 10, fontWeight: 600 }}
              />
            )}

            {/* Marcador vertical para "Fim do evento" (sempre na frente) */}
            {selectedEvent && eventEndLabel && (
              <ReferenceLine
                x={eventEndLabel}
                stroke="hsl(var(--foreground) / 0.5)"
                strokeDasharray="3 3"
                isFront={true}
                label={{
                  value: eventEndDate ? `Término (${format(eventEndDate, "dd/MM")})` : "Fim do evento",
                  position: "insideTopRight",
                  fill: "hsl(var(--foreground))",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}

            {/* Marcador vertical para "Liberação pós-evento (+1d)" (sempre na frente) */}
            {selectedEvent && postEventReleaseLabel && toReleaseAmount > 0 && !isRetroactiveEvent && (
              <ReferenceLine
                x={postEventReleaseLabel}
                stroke="hsl(142 71% 45% / 0.6)"
                strokeDasharray="2 2"
                isFront={true}
                label={{
                  value: `Liberação (+${formatBadgeBRL(toReleaseAmount)}) · ${truncateText(selectedEvent.event_title, 14)}`,
                  position: "insideBottomRight",
                  fill: "hsl(142 71% 45%)",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              />
            )}

            {/* Marcadores verticais de Resgate (Withdrawals) com evento e sem sobreposição */}
            {withdrawalGroups.map((group) => (
              <ReferenceLine
                key={`wd-group-${group.dateKey}`}
                x={group.dateLabel}
                stroke="hsl(0 84% 60% / 0.7)"
                strokeDasharray="2 2"
                isFront={true}
                label={(props) => {
                  const x = props.viewBox?.x ?? 0;
                  const chartWidth = props.viewBox?.width ?? 600;
                  const height = props.viewBox?.height ?? 160;
                  const chartY = props.viewBox?.y ?? 0;

                  // Base Y próxima ao rodapé da área de plotagem
                  const baseY = chartY + height - 26;
                  const isMultiple = group.withdrawals.length > 1;

                  if (isMultiple) {
                    const rowCount = Math.min(group.withdrawals.length, 3);
                    const cardHeight = 22 + rowCount * 14 + 6;
                    const cardY = baseY - cardHeight + 8;
                    const headerText = `Total resgates: -${formatBadgeBRL(group.totalAmount)} (${group.withdrawals.length} saques)`;
                    const cardWidth = Math.max(headerText.length * 6.2 + 18, 210);

                    const isNearRight = typeof props.viewBox?.width === "number" && (x + cardWidth + 8) > chartWidth;
                    const cardX = isNearRight ? x - cardWidth - 4 : x + 4;
                    const textX = isNearRight ? x - cardWidth + 6 : x + 12;

                    return (
                      <g style={{ pointerEvents: "auto" }}>
                        <title>{`Total de ${group.withdrawals.length} resgates em ${group.fullDate}: -${formatBRL(group.totalAmount)}`}</title>
                        <rect
                          x={cardX}
                          y={cardY}
                          width={cardWidth}
                          height={cardHeight}
                          rx={6}
                          fill="hsl(var(--background))"
                          fillOpacity={0.98}
                          stroke="hsl(0 84% 60% / 0.6)"
                          strokeWidth={1}
                        />
                        <text
                          x={textX}
                          y={cardY + 12}
                          fill="hsl(0 84% 50%)"
                          fontSize={9.5}
                          fontWeight={700}
                          dominantBaseline="central"
                        >
                          {headerText}
                        </text>
                        <line
                          x1={cardX + 6}
                          y1={cardY + 20}
                          x2={cardX + cardWidth - 6}
                          y2={cardY + 20}
                          stroke="hsl(0 84% 60% / 0.2)"
                          strokeWidth={1}
                        />
                        {group.withdrawals.slice(0, 3).map((w, wIdx) => {
                          const rowY = cardY + 29 + wIdx * 14;
                          return (
                            <text
                              key={w.id || wIdx}
                              x={textX + 2}
                              y={rowY}
                              fill="hsl(var(--foreground))"
                              fontSize={9}
                              fontWeight={500}
                              dominantBaseline="central"
                            >
                              {`• ${truncateText(w.eventTitle, 14)}: `}
                              <tspan fill="hsl(0 84% 50%)" fontWeight={600}>
                                -{formatBadgeBRL(w.amount)}
                              </tspan>
                            </text>
                          );
                        })}
                      </g>
                    );
                  }

                  // Saque individual único no dia
                  const w = group.withdrawals[0];
                  const badgeHeight = 18;
                  const badgeY = baseY - (group.baseLevel * (badgeHeight + 3));
                  const formatted = formatBadgeBRL(w.amount);
                  const text = `Resgate (-${formatted}) · ${truncateText(w.eventTitle, 16)}`;
                  const textWidth = text.length * 6.4 + 14;

                  const isNearRight = typeof props.viewBox?.width === "number" && (x + textWidth + 8) > chartWidth;
                  const badgeX = isNearRight ? x - textWidth - 4 : x + 4;
                  const textX = isNearRight ? x - textWidth + 2 : x + 10;

                  return (
                    <g style={{ pointerEvents: "auto" }}>
                      <title>{`Resgate de ${formatBRL(w.amount)} em ${w.fullDate}${w.time ? ` às ${w.time}` : ""} · Evento: ${w.eventTitle}${w.bankName ? ` (${w.bankName})` : ""}`}</title>
                      <rect
                        x={badgeX}
                        y={badgeY - 10}
                        width={textWidth}
                        height={badgeHeight}
                        rx={4}
                        fill="hsl(var(--background))"
                        fillOpacity={0.98}
                        stroke="hsl(0 84% 60% / 0.55)"
                        strokeWidth={1}
                      />
                      <text
                        x={textX}
                        y={badgeY - 10 + (badgeHeight / 2)}
                        fill="hsl(0 84% 50%)"
                        fontSize={9.5}
                        fontWeight={600}
                        dominantBaseline="central"
                      >
                        {text}
                      </text>
                    </g>
                  );
                }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda Apple Minimalista */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1 text-[11px] text-muted-foreground border-b border-border/25 pb-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-primary rounded-full" /><span>Saldo disponível</span></div>
          <div className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-b border-dashed border-primary" /><span>Previsão futura</span></div>
          <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /><span>Resgate (saque)</span></div>
          <div className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-foreground/60" /><span>Fim do evento</span></div>
        </div>
        <span className="text-[10px] text-muted-foreground/70">
          Linha do tempo baseada nas entradas (PIX D+2 e Cartão pós-evento) e saques efetuados
        </span>
      </div>

      {/* Bloco de Detalhes e Reconciliação do Evento */}
      {selectedEvent && eventEndDate ? (
        <div className="rounded-[24px] border border-border/50 bg-white/70 dark:bg-card/70 backdrop-blur-md p-4 sm:p-5 shadow-2xs space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-border/30">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-secondary/80 text-foreground/70 border border-border/30">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">
                  Reconciliação de saldo · {selectedEvent.event_title}
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Histórico de liquidações regulamentares e resgates efetuados
                </p>
              </div>
            </div>

            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium border shrink-0",
                isRetroactiveEvent
                  ? "bg-secondary/60 text-muted-foreground border-border/40"
                  : "bg-primary/10 text-primary border-primary/20"
              )}
            >
              {isRetroactiveEvent ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
                  <span>Evento concluído</span>
                </>
              ) : (
                <>
                  <Clock className="h-3 w-3 text-primary" />
                  <span>Faltam {Math.max(0, differenceInDays(eventEndDate, today))} dias</span>
                </>
              )}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
            {/* 1. Término */}
            <div className="flex flex-col justify-between rounded-2xl border border-border/40 bg-secondary/30 dark:bg-secondary/15 p-3.5 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  1. Término do evento
                </span>
                <Calendar className="h-3.5 w-3.5 text-muted-foreground/60" />
              </div>
              <div className="mt-2.5">
                <p className="font-display text-base sm:text-lg font-semibold tracking-tight text-foreground tabular-nums">
                  {format(eventEndDate, "dd 'de' MMM 'de' yyyy", { locale: ptBR })}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {isRetroactiveEvent ? "Realizado no passado" : "Data marcada"}
                </p>
              </div>
            </div>

            {/* 2. Total Liberado / A liberar no término */}
            <div className="flex flex-col justify-between rounded-2xl border border-border/40 bg-secondary/30 dark:bg-secondary/15 p-3.5 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {isRetroactiveEvent ? "2. Total liberado" : "2. A liberar no término"}
                </span>
                {isRetroactiveEvent ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600/70 dark:text-emerald-400/70" />
                ) : (
                  <Unlock className="h-3.5 w-3.5 text-amber-600/70 dark:text-amber-400/70" />
                )}
              </div>
              <div className="mt-2.5">
                <p className={cn(
                  "font-display text-base sm:text-lg font-semibold tracking-tight tabular-nums",
                  isRetroactiveEvent ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
                )}>
                  {formatBRL(isRetroactiveEvent ? summary.available + summary.withdrawn : toReleaseAmount)}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {isRetroactiveEvent ? "PIX (D+2) + Cartão pós-evento" : "Cartão e Boleto retidos"}
                </p>
              </div>
            </div>

            {/* 3. Total Resgatado / Previsão de liberação */}
            <div className="flex flex-col justify-between rounded-2xl border border-border/40 bg-secondary/30 dark:bg-secondary/15 p-3.5 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {isRetroactiveEvent ? "3. Total resgatado" : "3. Previsão de liberação"}
                </span>
                {isRetroactiveEvent ? (
                  <ArrowDownRight className="h-3.5 w-3.5 text-rose-600/70 dark:text-rose-400/70" />
                ) : (
                  <Calendar className="h-3.5 w-3.5 text-primary/70" />
                )}
              </div>
              <div className="mt-2.5">
                <p className={cn(
                  "font-display text-base sm:text-lg font-semibold tracking-tight tabular-nums",
                  isRetroactiveEvent ? "text-rose-600 dark:text-rose-400" : "text-foreground"
                )}>
                  {isRetroactiveEvent
                    ? formatBRL(summary.withdrawn)
                    : postEventReleaseDate
                    ? format(postEventReleaseDate, "dd 'de' MMM 'de' yyyy", { locale: ptBR })
                    : "—"}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {isRetroactiveEvent
                    ? summary.withdrawn > 0
                      ? "Saques pagos na conta"
                      : "Nenhum saque efetuado"
                    : "1 dia útil após término"}
                </p>
              </div>
            </div>

            {/* 4. Saldo Disponível Hoje */}
            <div className="flex flex-col justify-between rounded-2xl border border-border/60 bg-white dark:bg-card p-3.5 sm:p-4 shadow-2xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-foreground font-semibold">
                  4. Saldo disponível
                </span>
                <Wallet className="h-3.5 w-3.5 text-primary" />
              </div>
              <div className="mt-2.5">
                <p className="font-display text-base sm:text-lg font-semibold tracking-tight text-foreground tabular-nums">
                  {formatBRL(summary.available)}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {summary.available > 0 ? "Livre para novo saque" : "100% resgatado"}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-[24px] border border-border/50 bg-white/70 dark:bg-card/70 backdrop-blur-md p-4 sm:p-5 shadow-2xs space-y-4">
          <div className="flex items-center justify-between gap-2 pb-3 border-b border-border/30">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-secondary/80 text-foreground/70 border border-border/30">
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">
                  Próximos destravamentos de eventos futuros
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Datas em que cartões e boletos serão liberados para saque
                </p>
              </div>
            </div>
          </div>

          {upcomingReleases.length === 0 ? (
            <p className="py-3 flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span>Todos os eventos encerrados tiveram seus saldos liberados e reconciliados.</span>
            </p>
          ) : (
            <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {upcomingReleases.map((ev) => (
                <div key={ev.id} className="rounded-2xl border border-border/40 bg-secondary/30 dark:bg-secondary/15 p-3.5 space-y-2 text-xs">
                  <p className="font-medium text-foreground truncate">{ev.title}</p>
                  <div className="space-y-1 text-[11px]">
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>Fim do evento:</span>
                      <span className="font-medium text-foreground">{ev.endDate ? format(ev.endDate, "dd/MM/yyyy") : "—"}</span>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>Liberação (+1d):</span>
                      <span className="font-medium text-emerald-600 dark:text-emerald-400">{ev.releaseDate ? format(ev.releaseDate, "dd/MM/yyyy") : "—"}</span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-border/30 flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Valor a liberar:</span>
                    <span className="font-display font-semibold text-foreground text-sm tabular-nums">{formatBRL(ev.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
